import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft } from "lucide-react";

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-background py-8 md:py-16">
      <SEO
        title="Privatlivspolitik | MinFesttale.dk"
        description="Sådan håndterer vi dine persondata. Læs hvilke oplysninger vi indsamler, hvordan vi opbevarer dem og dine rettigheder under GDPR."
        path="/privatlivspolitik"
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* Back button */}
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ChevronLeft className="h-4 w-4 mr-2" />
            Tilbage til forsiden
          </Button>
        </Link>

        <Card className="border-border/40">
          <CardContent className="p-6 md:p-10">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-2">
              Privatlivspolitik
            </h1>
            <p className="text-sm text-muted-foreground mb-8">
              Sidst opdateret: Februar 2026
            </p>

            <div className="prose prose-slate max-w-none space-y-6">
              <section>
                <h2 className="font-heading text-xl font-bold text-primary mt-6 mb-3">
                  1. Dataansvarlig
                </h2>
                <p className="text-muted-foreground">
                  LearnIQ ApS<br />
                  CVR: 42111007<br />
                  Adresse: Amaliegade 6, 2. sal, 1256 København K<br />
                  E-mail: hej@minfesttale.dk<br />
                  <span className="text-sm italic">Kundeservice kun via e-mail — ingen telefonisk support. Vi svarer typisk inden for 24 timer.</span>
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl font-bold text-primary mt-6 mb-3">
                  2. Hvilke oplysninger indsamler vi?
                </h2>
                <p className="text-muted-foreground mb-3">
                  Vi indsamler følgende personoplysninger:
                </p>
                
                <p className="text-muted-foreground font-semibold mb-2">Oplysninger du giver os via formularen:</p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-1 mb-3">
                  <li>Navne (dit navn, personens navn, evt. andre navne nævnt i anekdoter)</li>
                  <li>Relation til personen</li>
                  <li>Anekdoter og personlige historier</li>
                  <li>Personens egenskaber og interesser</li>
                  <li>Ønsker og budskab</li>
                </ul>

                <p className="text-muted-foreground font-semibold mb-2">Betalingsoplysninger:</p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-1 mb-3">
                  <li>E-mailadresse</li>
                  <li>Betalingsinformation håndteres af Stripe og opbevares ikke hos os</li>
                </ul>

                <p className="text-muted-foreground font-semibold mb-2">Tekniske oplysninger:</p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                  <li>IP-adresse</li>
                  <li>Browsertype og enhed</li>
                  <li>Cookies (se afsnit 7)</li>
                </ul>
              </section>

              <section>
                <h2 className="font-heading text-xl font-bold text-primary mt-6 mb-3">
                  3. Hvad bruger vi oplysningerne til?
                </h2>
                <p className="text-muted-foreground mb-3">
                  Dine oplysninger bruges til at:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-1 mb-3">
                  <li>Generere din personlige tale via AI (Claude, leveret af Anthropic)</li>
                  <li>Behandle din betaling</li>
                  <li>Sende dig din kvittering og downloadlink</li>
                  <li>Levere tilfredshedsgarantien (regenerering inden for 24 timer)</li>
                </ul>
                <p className="text-muted-foreground">
                  Vi bruger IKKE dine oplysninger til markedsføring, med mindre du aktivt har samtykket til dette.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl font-bold text-primary mt-6 mb-3">
                  4. AI-generering
                </h2>
                <p className="text-muted-foreground mb-3">
                  Vi bruger AI-teknologi (Claude fra Anthropic) til at generere din tale. Det betyder at dine formularoplysninger (navne, anekdoter osv.) sendes til Anthropic's API for at generere teksten.
                </p>
                <p className="text-muted-foreground">
                  Anthropic opbevarer ikke dine data efter generering og bruger dem ikke til at træne deres AI-modeller. Se Anthropic's privatlivspolitik for yderligere detaljer.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl font-bold text-primary mt-6 mb-3">
                  5. Deling af data
                </h2>
                <p className="text-muted-foreground mb-3">
                  Vi deler dine oplysninger med følgende tredjeparter:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-1 mb-3">
                  <li><strong>Anthropic (Claude AI):</strong> For at generere din tale. Data bruges kun til generering og opbevares ikke.</li>
                  <li><strong>Stripe:</strong> For at behandle din betaling. Stripe har sin egen privatlivspolitik.</li>
                </ul>
                <p className="text-muted-foreground">
                  Vi sælger, udlejer eller videregiver ALDRIG dine personoplysninger til andre tredjeparter.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl font-bold text-primary mt-6 mb-3">
                  6. Opbevaring og sletning
                </h2>
                <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                  <li>Dine formulardata opbevares i op til 30 dage efter køb, så vi kan levere tilfredshedsgarantien og evt. support.</li>
                  <li>Herefter slettes dine formulardata automatisk.</li>
                  <li>Betalingsdata opbevares i henhold til bogføringsloven (5 år).</li>
                  <li>Du kan til enhver tid bede om sletning af dine data ved at kontakte os på hej@minfesttale.dk.</li>
                </ul>
              </section>

              <section>
                <h2 className="font-heading text-xl font-bold text-primary mt-6 mb-3">
                  7. Cookies
                </h2>
                <p className="text-muted-foreground mb-3">
                  Vi bruger følgende cookies og lignende teknologier:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-1 mb-3">
                  <li><strong>Nødvendige cookies:</strong> For at hjemmesiden fungerer korrekt (login-session, formular-autosave, indkøbsflow). Disse kræver ikke samtykke.</li>
                  <li><strong>Statistik:</strong> Google Analytics 4 — anonymiseret IP — for at forstå hvordan besøgende bruger sitet. Aktiveres kun med dit samtykke.</li>
                  <li><strong>Markedsføring:</strong> Meta (Facebook) Pixel — bruges til at måle effekten af eventuelle annoncer og vise relevante anbefalinger. Aktiveres kun med dit samtykke.</li>
                </ul>
                <p className="text-muted-foreground">
                  Når du første gang besøger MinFesttale.dk, vises en cookie-banner hvor du kan vælge &ldquo;Kun nødvendige&rdquo; eller &ldquo;Acceptér alle&rdquo;. Dit valg gemmes lokalt i 12 måneder. Du kan altid trække dit samtykke tilbage ved at rydde browserens lokale data og genindlæse siden, eller ved at kontakte os på <a href="mailto:hej@minfesttale.dk" className="text-secondary underline">hej@minfesttale.dk</a>.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl font-bold text-primary mt-6 mb-3">
                  8. Dine rettigheder (GDPR)
                </h2>
                <p className="text-muted-foreground mb-3">
                  Du har ret til at:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-1 mb-3">
                  <li><strong>Få indsigt</strong> i de personoplysninger vi behandler om dig</li>
                  <li><strong>Få rettet</strong> urigtige oplysninger</li>
                  <li><strong>Få slettet</strong> dine oplysninger ("retten til at blive glemt")</li>
                  <li><strong>Trække samtykke tilbage</strong> til enhver tid</li>
                  <li><strong>Klage</strong> til Datatilsynet (datatilsynet.dk)</li>
                </ul>
                <p className="text-muted-foreground">
                  Kontakt os på hej@minfesttale.dk for at gøre brug af dine rettigheder. Vi svarer inden for 30 dage.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl font-bold text-primary mt-6 mb-3">
                  9. Sikkerhed
                </h2>
                <p className="text-muted-foreground">
                  Vi anvender kryptering (HTTPS/SSL) på hele sitet. Betalingsoplysninger håndteres udelukkende af Stripe, som er PCI DSS-certificeret.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl font-bold text-primary mt-6 mb-3">
                  10. Ændringer
                </h2>
                <p className="text-muted-foreground">
                  Vi forbeholder os retten til at opdatere denne privatlivspolitik. Ved væsentlige ændringer informerer vi dig via sitet.
                </p>
              </section>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPage;
